// Shared automata utility functions used across components

const EPSILON_SYMBOLS = new Set(['ε', 'λ']);

/** Unicode superscript mapping */
const SUPERSCRIPT_MAP = {
  '0': '\u2070', '1': '\u00B9', '2': '\u00B2', '3': '\u00B3', '4': '\u2074',
  '5': '\u2075', '6': '\u2076', '7': '\u2077', '8': '\u2078', '9': '\u2079',
  'n': '\u207F', 'm': '\u1D50', 'i': '\u2071', 'j': '\u02B2', 'k': '\u1D4F',
  'l': '\u02E1', 'p': '\u1D56', 'a': '\u1D43', 'b': '\u1D47', 'c': '\u1D9C',
  'd': '\u1D48', 'e': '\u1D49', 'f': '\u1DA0', 'g': '\u1D4D', 'h': '\u02B0',
  'o': '\u1D52', 'r': '\u02B3', 's': '\u02E2', 't': '\u1D57', 'u': '\u1D58',
  'v': '\u1D5B', 'w': '\u02B7', 'x': '\u02E3', 'y': '\u02B8', 'z': '\u1DBB',
  '+': '\u207A', '-': '\u207B', '=': '\u207C', '(': '\u207D', ')': '\u207E',
  '*': '\u002A',
};

/** Convert ^x or ^{xyz} to Unicode superscript characters */
export function applySuperscripts(text) {
  // ^{...} multi-char superscript
  let result = text.replace(/\^{([^}]*)}/g, (_, chars) =>
    [...chars].map(c => SUPERSCRIPT_MAP[c] || c).join('')
  );
  // ^x single-char superscript
  result = result.replace(/\^([0-9a-zA-Z+\-=()* ])/g, (_, c) =>
    SUPERSCRIPT_MAP[c.toLowerCase()] || `^${c}`
  );
  return result;
}


/** Quick DFA/NFA classification */
export function classifyAutomaton(a) {
  if (a.type === 'PDA') return 'PDA';
  if (a.type === 'TM') return 'TM';
  const { states = [], transitions = [] } = a;
  if (!states.length) return 'NFA';
  if (states.filter(s => s.is_initial).length !== 1) return 'NFA';
  const alphabet = new Set();
  for (const t of transitions) {
    for (const sym of t.symbols) {
      if (EPSILON_SYMBOLS.has(sym)) return 'NFA';
      alphabet.add(sym);
    }
  }
  if (!alphabet.size) return 'NFA';
  for (const state of states) {
    const symDests = {};
    for (const t of transitions) {
      if (t.from_state === state.id) {
        for (const sym of t.symbols) {
          if (!symDests[sym]) symDests[sym] = new Set();
          symDests[sym].add(t.to_state);
        }
      }
    }
    for (const sym of alphabet) {
      if (!symDests[sym] || symDests[sym].size !== 1) return 'NFA';
    }
  }
  return 'DFA';
}

/** Build transition lookup table: { lookup, columns } */
export function buildTransitionTable(states, transitions) {
  const alphabet = [...new Set(
    transitions.flatMap(t => t.symbols).filter(s => !EPSILON_SYMBOLS.has(s))
  )].sort();
  const epsilonSyms = [...new Set(
    transitions.flatMap(t => t.symbols).filter(s => EPSILON_SYMBOLS.has(s))
  )];
  const columns = [...alphabet, ...epsilonSyms];

  const lookup = {};
  for (const t of transitions) {
    for (const sym of t.symbols) {
      const key = `${t.from_state}|${sym}`;
      if (!lookup[key]) lookup[key] = new Set();
      const dest = states.find(s => s.id === t.to_state)?.name || '?';
      lookup[key].add(dest);
    }
  }

  return { lookup, columns };
}

const LAMBDA = '\u03BB';

/** Get structured spec items: [{ label, tooltip, value }] */
export function getSpecificationItems(a) {
  if (!a) return [];
  const q = a.states.length > 0 ? `{${a.states.map(s => s.name).join(', ')}}` : '∅';
  const sigma = a.alphabet.length > 0 ? `{${a.alphabet.join(', ')}}` : '∅';
  const q0 = a.states.find(s => s.is_initial)?.name || '—';
  const finals = a.states.filter(s => s.is_final).map(s => s.name);
  const f = finals.length > 0 ? `{${finals.join(', ')}}` : '∅';

  if (a.type === 'PDA') {
    const stackSymbols = new Set();
    if (a.initial_stack_symbol) stackSymbols.add(a.initial_stack_symbol);
    for (const t of (a.transitions || [])) {
      for (const r of (t.pda_rules || [])) {
        if (r.pop && !EPSILON_SYMBOLS.has(r.pop) && r.pop !== LAMBDA) stackSymbols.add(r.pop);
        if (r.push) {
          for (const ch of r.push) {
            if (!EPSILON_SYMBOLS.has(ch) && ch !== LAMBDA) stackSymbols.add(ch);
          }
        }
      }
    }
    const gamma = stackSymbols.size > 0 ? `{${[...stackSymbols].sort().join(', ')}}` : '∅';
    const z0 = a.initial_stack_symbol || 'Z';
    return [
      { label: 'Q', tooltip: 'Set of states', value: q },
      { label: '\u03A3', tooltip: 'Input alphabet', value: sigma },
      { label: '\u0393', tooltip: 'Stack alphabet', value: gamma },
      { label: 'q', tooltip: 'Initial state', value: q0 },
      { label: 'z', tooltip: 'Initial stack symbol', value: z0, editable: true },
      { label: 'F', tooltip: 'Set of accepting states', value: f },
    ];
  }

  if (a.type === 'TM') {
    const tapeSymbols = new Set(a.alphabet || []);
    tapeSymbols.add(a.blank_symbol || '\u25A1');
    for (const t of (a.transitions || [])) {
      for (const r of (t.tm_rules || [])) {
        if (r.read) tapeSymbols.add(r.read);
        if (r.write) tapeSymbols.add(r.write);
      }
    }
    const gamma = tapeSymbols.size > 0 ? `{${[...tapeSymbols].sort().join(', ')}}` : '\u2205';
    const acceptStates = a.states.filter(s => s.is_final).map(s => s.name);
    const rejectStates = a.states.filter(s => s.is_reject).map(s => s.name);
    const qAcc = acceptStates.length > 0 ? acceptStates.join(', ') : '\u2014';
    const qRej = rejectStates.length > 0 ? rejectStates.join(', ') : '\u2014';
    return [
      { label: 'Q', tooltip: 'Set of states', value: q },
      { label: '\u03A3', tooltip: 'Input alphabet', value: sigma },
      { label: '\u0393', tooltip: 'Tape alphabet', value: gamma },
      { label: 'q\u2080', tooltip: 'Initial state', value: q0 },
      { label: 'q\u2090', tooltip: 'Accept state', value: qAcc },
      { label: 'q\u1D63', tooltip: 'Reject state', value: qRej },
      { label: '\u25A1', tooltip: 'Blank symbol', value: a.blank_symbol || '\u25A1', editable: 'blank_symbol' },
      { label: 'Limit', tooltip: 'Step limit before halting', value: String(a.step_limit || 10000), editable: 'step_limit' },
    ];
  }

  return [
    { label: 'Q', tooltip: 'Set of states', value: q },
    { label: '\u03A3', tooltip: 'Input alphabet', value: sigma },
    { label: 'q', tooltip: 'Initial state', value: q0 },
    { label: 'F', tooltip: 'Set of accepting states', value: f },
  ];
}

/** Plain text specification (for clipboard copy) */
export function getSpecificationText(a) {
  return getSpecificationItems(a).map(({ label, value }) => `${label} = ${value}`).join('\n');
}

/** Plain text transition function */
export function getTransitionFunctionText(a) {
  if (!a) return '';

  if (a.type === 'TM') {
    const lines = [];
    for (const t of (a.transitions || [])) {
      const from = a.states.find(s => s.id === t.from_state)?.name || '?';
      const to = a.states.find(s => s.id === t.to_state)?.name || '?';
      for (const r of (t.tm_rules || [])) {
        const read = r.read || '\u25A1';
        const write = r.write || '\u25A1';
        lines.push(`\u03B4(${from}, ${read}) = (${to}, ${write}, ${r.direction})`);
      }
    }
    return lines.join('\n');
  }

  if (a.type === 'PDA') {
    const lines = [];
    for (const t of (a.transitions || [])) {
      const from = a.states.find(s => s.id === t.from_state)?.name || '?';
      const to = a.states.find(s => s.id === t.to_state)?.name || '?';
      for (const r of (t.pda_rules || [])) {
        const inp = r.input || LAMBDA;
        const pop = r.pop || LAMBDA;
        const push = r.push || LAMBDA;
        lines.push(`δ(${from}, ${inp}, ${pop}) = (${to}, ${push})`);
      }
    }
    return lines.join('\n');
  }

  return a.transitions.map(t => {
    const from = a.states.find(s => s.id === t.from_state)?.name || '?';
    const to = a.states.find(s => s.id === t.to_state)?.name || '?';
    return `\u03B4(${from}, {${collapseRanges(t.symbols)}}) = ${to}`;
  }).join('\n');
}

/** Build PDA transition list data */
export function buildPdaTransitionList(states, transitions) {
  const rows = [];
  for (const t of transitions) {
    const from = states.find(s => s.id === t.from_state)?.name || '?';
    const to = states.find(s => s.id === t.to_state)?.name || '?';
    for (const r of (t.pda_rules || [])) {
      rows.push({
        from,
        to,
        input: r.input || LAMBDA,
        pop: r.pop || LAMBDA,
        push: r.push || LAMBDA,
      });
    }
  }
  return rows;
}

/** Build TM transition list data */
export function buildTmTransitionList(states, transitions) {
  const rows = [];
  for (const t of transitions) {
    const from = states.find(s => s.id === t.from_state)?.name || '?';
    const to = states.find(s => s.id === t.to_state)?.name || '?';
    for (const r of (t.tm_rules || [])) {
      rows.push({
        from,
        to,
        read: r.read || '\u25A1',
        write: r.write || '\u25A1',
        direction: r.direction || 'R',
      });
    }
  }
  return rows;
}

/** Snap coordinate to grid */
export function snap(val, gridSize = 40) {
  return Math.round(val / gridSize) * gridSize;
}


/**
 * Expand range notation in symbol lists.
 * '0..9' → ['0','1',...,'9'], 'a..f' → ['a','b','c','d','e','f']
 * Only expands valid same-type ranges (digit-digit, lower-lower, upper-upper).
 */
export function expandRanges(rawSymbols) {
  const isDigit = c => c >= '0' && c <= '9';
  const isLower = c => c >= 'a' && c <= 'z';
  const isUpper = c => c >= 'A' && c <= 'Z';
  const sameType = (a, b) => (isDigit(a) && isDigit(b)) || (isLower(a) && isLower(b)) || (isUpper(a) && isUpper(b));

  const result = [];
  for (const sym of rawSymbols) {
    const m = sym.match(/^(.)\.\.(.)/);
    if (m && sameType(m[1], m[2])) {
      const start = m[1].charCodeAt(0);
      const end = m[2].charCodeAt(0);
      const step = start <= end ? 1 : -1;
      for (let c = start; step > 0 ? c <= end : c >= end; c += step) {
        result.push(String.fromCharCode(c));
      }
    } else {
      result.push(sym);
    }
  }
  return [...new Set(result)];
}

/**
 * Collapse consecutive single-char symbols into range notation.
 * ['0','1',...,'9'] → '0..9', ['a','b','c'] → 'a..c'
 * Ranges of 3+ chars are collapsed; shorter runs are kept as-is.
 */
export function collapseRanges(symbols) {
  if (!symbols || symbols.length === 0) return '';
  const singles = symbols.filter(s => s.length === 1).sort();
  const multis = symbols.filter(s => s.length > 1);
  const groups = [];
  let i = 0;
  while (i < singles.length) {
    const start = singles[i];
    let end = start;
    while (i + 1 < singles.length && singles[i + 1].charCodeAt(0) === singles[i].charCodeAt(0) + 1) {
      i++;
      end = singles[i];
    }
    if (end.charCodeAt(0) - start.charCodeAt(0) >= 2) {
      groups.push(`${start}..${end}`);
    } else if (end !== start) {
      groups.push(start);
      groups.push(end);
    } else {
      groups.push(start);
    }
    i++;
  }
  return [...groups, ...multis].join(', ');
}
